import { GtmhubController } from "@gtmhub/core";
import { IComponentOptions } from "angular";
import { IGtmhubRootScopeService, RequireAtLeastOne } from "@gtmhub/models";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IGoal } from "@gtmhub/goals/models";
import {
  filterActiveAndFutureSessions,
  getSessionAndOneLevelChildren,
  IPlanningSession,
  ISessionsStoreState,
  PlanningSessionsActions,
  PlanningSessionSelector,
} from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { IModalService } from "angular-ui-bootstrap";
import { IModalComponent } from "@gtmhub/core/routing";
import { goalConfig, metricConfig } from "./configs";
import { IMetric } from "@gtmhub/okrs/metrics";
import { CloneOkrsService } from "@gtmhub/okrs";
import { IGoalCloneSelected } from "@gtmhub/goals/controllers/clone-or-move-goal";
import { CustomFieldActions, ICustomField, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

export interface IMetricCloneSelected {
  progress: boolean;
  notifications?: boolean;
  duplicateInsight?: boolean;
  sync?: boolean;
  session: IPlanningSession;
  goal: IGoal;
}

const bindStateToThis = (state: ISessionsStoreState & ICustomFieldsStoreState): ICloneOkrsRedux => ({
  activeSessions: filterActiveAndFutureSessions(state.sessions.items),
  metricCustomFields: state.customFields.map["metric"] || [],
  goalCustomFields: state.customFields.map["goal"] || [],
  reduxFetched: state.customFields.isFetched && state.sessions.isFetched,
  reduxError: state.customFields.error || state.sessions.error,
});

interface ICloneOkrsRedux {
  activeSessions: IPlanningSession[];
  metricCustomFields: ICustomField[];
  goalCustomFields: ICustomField[];
  reduxFetched: boolean;
  reduxError: IUIError;
}

export interface ICloneConfig {
  titleKey: string;
  opts: {
    displayName: string;
    fieldName: string;
    tooltip?: string;
  }[];
  selectFrom: string;
}

export type CloneOkrsMode = "OKR" | "KR";

type CloneOkrsModalBindings = { mode: CloneOkrsMode; item: IGoal | IMetric };

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ICloneOkrsBindings extends IModalComponent<CloneOkrsModalBindings> {}

export interface CloneOkrsCtrl extends ICloneOkrsBindings, ICloneOkrsRedux {}
export class CloneOkrsCtrl extends GtmhubController {
  selected: RequireAtLeastOne<IGoalCloneSelected | IMetricCloneSelected> = {
    progress: true,
  };
  disabledOpts: Record<string, boolean> = {};
  config: ICloneConfig;
  validSessions: IPlanningSession[] = [];
  chooseBetweenSessions: boolean;

  static $inject = [
    "$rootScope",
    "$ngRedux",
    "PlanningSessionsActions",
    "CustomFieldActions",
    "GoalModalsService",
    "PlanningSessionSelector",
    "CloneOkrsService",
    "$scope",
    "UIErrorHandlingService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private customFieldActions: CustomFieldActions,
    private goalModalsService: GoalModalsService,
    private planningSessionSelector: PlanningSessionSelector,
    private cloneOkrsService: CloneOkrsService,
    private $scope: IExtendedRootScopeService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super();
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect(bindStateToThis)(this));
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());

    const withDuplicateInsightOpt = (this.resolve.item as IGoal)?.metrics?.some((metric) => metric.insightName) || !!(this.resolve.item as IMetric)?.insightName;
    this.disabledOpts.progress = withDuplicateInsightOpt;
    this.config =
      this.resolve.mode === "OKR" ? goalConfig(withDuplicateInsightOpt) : metricConfig(withDuplicateInsightOpt, this.$rootScope.featureAvailable("okrs.mirror-krs"));

    for (const opt of this.config.opts) {
      this.selected[opt.fieldName] = false;
      this.disabledOpts[opt.fieldName] = false;
    }
  }

  set(opt: string, value: boolean): void {
    this.selected[opt] = value;

    this.validateDisabled(opt);
  }

  openGoalSelector(): void {
    this.$rootScope.traceAction("open_goal_selector_modal", () => {
      const goalSelected = (goal: IGoal) => {
        this.selected.session && delete this.selected.session;
        this.selected.goal = goal;
        const validSessions = getSessionAndOneLevelChildren(this.activeSessions, goal.sessionId);

        if (validSessions.length === 1 || this.resolve.mode === "KR") {
          this.selected.session = validSessions[0];
          this.chooseBetweenSessions = false;
        } else {
          this.chooseBetweenSessions = true;
        }
      };

      const sessionSelected = (session: IPlanningSession): void => {
        this.selected.goal && delete this.selected.goal;

        this.selected.session = session;
        this.chooseBetweenSessions = true;
      };

      this.goalModalsService.openGoalSelectModal({ unselectableId: null, invokedBy: this.resolve.mode === "OKR" ? "CloneOKR" : "KR" }).then((selection) => {
        if (selection.type === "goal") {
          goalSelected(selection.goal);
        }

        if (selection.type === "session") {
          sessionSelected(selection.session);
        }
      });
    });
  }

  openSessionSelector(): void {
    this.$rootScope.traceAction("open_session_selector_modal", () => {
      this.planningSessionSelector.selectSession({ parentSessionId: (this.selected.goal as IGoal).sessionId }).then((session) => {
        this.selected.session = session;
      });
    });
  }

  startCloning(): void {
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxFetched,
        () => this.reduxError
      )
      .then(
        () => {
          const customFields = this.resolve.mode == "KR" ? this.metricCustomFields : this.goalCustomFields;
          const shouldConfirm = customFields
            .filter((cf) => cf.targetIds)
            .some((cf) => cf.targetIds.includes(this.resolve.item.sessionId) && !cf.targetIds.includes(this.selected.session.id));

          if (shouldConfirm) {
            const cfCount = this.resolve.item.customFields && Object.values(this.resolve.item.customFields).length;

            this.cloneOkrsService.confirmClone({ cfCount, mode: this.resolve.mode }).then(() => {
              this.clone();
            });
          } else {
            this.clone();
          }
        },
        () => this.uiErrorHandlingService.handleModal(this.reduxError)
      );
  }

  private clone(): void {
    if (this.resolve.mode === "KR") {
      this.cloneOkrsService.cloneMetric({ metric: this.resolve.item as IMetric, opts: this.selected as IMetricCloneSelected });
    } else {
      this.cloneOkrsService.cloneGoal({ goal: this.resolve.item as IGoal, opts: this.selected as IGoalCloneSelected });
    }

    this.close();
  }

  private validateDisabled(opt: string): void {
    const isDisabled = this.selected[opt] as boolean;

    if (opt === "duplicateInsight") {
      this.disabledOpts.sync = isDisabled;
    }

    if (opt === "sync" && "duplicateInsight" in this.disabledOpts) {
      this.disabledOpts.duplicateInsight = isDisabled;
    }

    if (opt === "progress") {
      if (!isDisabled) {
        (this.selected as IMetricCloneSelected).sync = false;
        this.disabledOpts.sync = true;
      } else {
        this.disabledOpts.sync = false;
      }
    }
  }
}

export class CloneOkrsModal {
  static $inject = ["$uibModal"];

  constructor(private $uibModal: IModalService) {}

  openForGoalClone(item: IGoal): void {
    this.$uibModal.open({
      component: "cloneOkrs",
      resolve: {
        item: () => item,
        mode: () => "OKR",
      },
      windowClass: "medium clone",
      backdrop: true,
    });
  }

  openForMetricClone(item: IMetric): void {
    this.$uibModal.open({
      component: "cloneOkrs",
      resolve: {
        item: () => item,
        mode: () => "KR",
      },
      windowClass: "medium clone",
      backdropClass: "dark-backdrop",
      backdrop: true,
    });
  }
}

export const CloneOkrsComponent: IComponentOptions = {
  controller: CloneOkrsCtrl,
  templateUrl: require("./clone-okrs.jade"),
  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },
};
